import React from "react";
import { TextField } from "@material-ui/core";
import { Form, Field } from "react-final-form";
import { IntroText, FormActionDrawer } from "../../../components";
import createDecorator from "final-form-focus";
import { string, object } from "yup";
import { useYupValidation } from "../../../hooks";
import { formatDate } from "../../../utils";
import { useTranslation } from "react-i18next";

const focusOnErrors = createDecorator();

const validationSchema = object({
  flight_airline: string()
    .required()
    .trim()
    .label("Airline"),
  flight_number: string()
    .trim()
    .label("Flight number"),
  flight_origin: string()
    .required()
    .trim()
    .label("Flight origin"),
  flight_date: string()
    .required()
    .trim()
    .label("Flight date"),
  flight_destination: string()
    .required()
    .trim()
    .label("Flight destination")
});

const ScheduleFlightInfo = ({
  setDrawerClearance,
  updateScheduleData,
  scheduleData,
  isSubmitting
}) => {
  const { t } = useTranslation();
  const { validate } = useYupValidation();

  const validateForm = async data => {
    const errors = await validate(validationSchema)(data);

    if (errors) return errors;

    updateScheduleData({
      additionalInfo: { ...scheduleData.additionalInfo, ...data }
    });
  };

  return (
    <Form
      onSubmit={validateForm}
      decorators={[focusOnErrors]}
      render={({ handleSubmit, submitErrors: errors = {} }) => (
        <form data-cy="schedule-screen-flight-info" onSubmit={handleSubmit}>
          <IntroText>{t("schedule.flight_info.intro_text")}</IntroText>
          <Field
            name="flight_airline"
            render={({ input }) => (
              <TextField
                {...input}
                label={t("schedule.flight_info.form.flight_airline.label")}
                fullWidth
                margin="normal"
                variant="outlined"
                error={!!errors.flight_airline}
                helperText={errors.flight_airline}
                inputProps={{
                  "data-lpignore": "true",
                  "data-cy": "input-flight_airline"
                }}
              />
            )}
          />
          <Field
            name="flight_number"
            render={({ input }) => (
              <TextField
                {...input}
                label={t("schedule.flight_info.form.flight_number.label")}
                fullWidth
                margin="normal"
                variant="outlined"
                error={!!errors.flight_number}
                helperText={errors.flight_number}
                inputProps={{
                  "data-lpignore": "true",
                  "data-cy": "input-flight_number"
                }}
              />
            )}
          />
          <Field
            name="flight_date"
            parse={value => formatDate(value, dateFormat.default, "YYYY-MM-DD")}
            format={value =>
              formatDate(value, "YYYY-MM-DD", dateFormat.default)
            }
            render={({ input }) => (
              <NumberFormat
                {...input}
                format="##/##/####"
                placeholder={dateFormat.default}
                label={t("schedule.flight_info.form.flight_date.label")}
                fullWidth
                margin="normal"
                variant="outlined"
                error={!!errors.flight_date}
                helperText={errors.flight_date}
                customInput={TextField}
                inputProps={{
                  "data-cy": "input-flight_date"
                }}
              />
            )}
          />
          <Field
            name="flight_origin"
            render={({ input }) => (
              <TextField
                {...input}
                label={t("schedule.flight_info.form.flight_origin.label")}
                fullWidth
                margin="normal"
                variant="outlined"
                error={!!errors.flight_origin}
                helperText={errors.flight_origin}
                inputProps={{
                  "data-lpignore": "true",
                  "data-cy": "input-flight_origin"
                }}
              />
            )}
          />
          <Field
            name="flight_destination"
            render={({ input }) => (
              <TextField
                {...input}
                label={t("schedule.flight_info.form.flight_destination.label")}
                fullWidth
                margin="normal"
                variant="outlined"
                error={!!errors.flight_destination}
                helperText={errors.flight_destination}
                inputProps={{
                  "data-lpignore": "true",
                  "data-cy": "input-flight_destination"
                }}
              />
            )}
          />
          <FormActionDrawer
            setDrawerClearance={setDrawerClearance}
            loading={isSubmitting}
          />
        </form>
      )}
    />
  );
};

export { ScheduleFlightInfo };
